export interface CartItem {
  id: string;
  productId: string;
  option: string;
  quantity: number;
  valid: boolean;
  errors: string[];
  basePrice: number;
  product: {
    id: string;
    name: string;
    brand: {
      name: string;
    };
  };
  discounts: {
    total: number;
  };
  taxes: {
    total: number;
    cannabis: number;
    sales: number;
  };
  // Enhanced pricing information for discount display
  originalPricing?: {
    regularPrice: number;
    specialPrice?: number;
    pricingType: 'MEDICAL' | 'RECREATIONAL';
  };
}

export interface CartState {
  id: string;
  createdAt: string;
  updatedAt: string;
  orderType: string;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  redirectUrl?: string;
  items: CartItem[];
  priceSummary: {
    subtotal: number;
    total: number;
    taxes: number;
    fees: number;
    discounts: number;
    rewards: number;
    mixAndMatch: number;
  };
}

interface CartResponse {
  success: boolean;
  data: CartState;
  error?: string;
}

export class CartService {
  private static baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cart`;

  /**
   * Create a new cart
   */
  public static async createCart(pricingType: 'MEDICAL' | 'RECREATIONAL' = 'RECREATIONAL', retailerId?: string): Promise<CartState> {
    try {
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pricingType,
          retailerId,
          trackedCartMetaData: {
            customerInfo: {
              email: 'customer@example.com',
              firstName: 'Customer',
              lastName: 'User'
            }
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const err = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        (err as any).status = response.status;
        throw err;
      }

      const data: CartResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  /**
   * Add item to cart
   */
  public static async addItem(
    cartId: string,
    productId: string,
    option: string,
    quantity: number,
    retailerId: string
  ): Promise<CartState> {
    try {
      const response = await fetch(`${this.baseUrl}/${cartId}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId,
          option,
          quantity,
          retailerId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        // Handle the specific error format from your backend
        let errorMessage = errorData.error || `HTTP error! status: ${response.status}`;

        // If we have the full error object, try to extract a cleaner message
        if (errorData.message) {
          // Extract the clean message before any colon and JSON
          errorMessage = errorData.message.split(':')[0].trim();
        }

        const err = new Error(errorMessage);
        (err as any).status = response.status;
        throw err;
      }

      const data: CartResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error;
    }
  }

  /**
   * Update item quantity
   */
  public static async updateQuantity(
    cartId: string,
    itemId: string,
    quantity: number,
    retailerId: string
  ): Promise<CartState> {
    try {
      const response = await fetch(`${this.baseUrl}/${cartId}/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity,
          retailerId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const err = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        (err as any).status = response.status;
        throw err;
      }

      const data: CartResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating item quantity:', error);
      throw error;
    }
  }

  /**
   * Remove item from cart
   */
  public static async removeItem(cartId: string, itemId: string, retailerId: string): Promise<CartState> {
    try {
      const response = await fetch(`${this.baseUrl}/${cartId}/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          retailerId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const err = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        (err as any).status = response.status;
        throw err;
      }

      const data: CartResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error;
    }
  }

  /**
   * Get cart details
   */
  public static async getCart(cartId: string, retailerId: string): Promise<CartState> {
    try {
      const response = await fetch(`${this.baseUrl}/${cartId}?retailerId=${encodeURIComponent(retailerId)}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const err = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        (err as any).status = response.status;
        throw err;
      }

      const data: CartResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error getting cart:', error);
      throw error;
    }
  }
}